import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";
import { useMemo, useState } from "react";

import { MUTATIONS, QUERIES } from "../../../../graphql";
import {
  Apartment,
  Building,
  computeUserVisualName,
  Coupon,
  CouponApartment,
  CouponBuilding,
  CouponService,
  CouponServiceListItem,
  CouponTargetUser,
  CouponType,
  Service,
  ServiceTypeListItem,
  User,
} from "../../../../graphql/models";
import { useSnackbarMutation } from "../../../../hooks";
import { i18nForFields } from "../../../../i18n";
import {
  ConfirmButtonWithDialog,
  CouponUsageDetails,
  GqlRecordPicker,
  I18NTextField,
} from "../../../components";

import useCouponFormStyles from "./coupons_form.style";

export interface CouponEditFormProps {
  value: Coupon | undefined;
  onValueDeleted: () => void;
  onValueUpdatedSuccessfully: (v: Coupon) => void;
}

export function CouponEditForm(props: CouponEditFormProps) {
  const { value, onValueDeleted, onValueUpdatedSuccessfully } = props;
  const classes = useCouponFormStyles();

  let mutation = MUTATIONS.CREATE_COUPON;
  if (value && value?.id) {
    mutation = MUTATIONS.UPDATE_COUPON;
  }

  const [invokeCreateOrUpdate, { loading }] = useSnackbarMutation(mutation, {
    onCompleted: (_) => {
      // Todo: this is slightly bad.
      onValueUpdatedSuccessfully(value as Coupon);
    },
  });

  const [invokeDeleteCoupon, { loading: deletingCoupon }] = useSnackbarMutation(
    MUTATIONS.DELETE_COUPON,
    {
      onCompleted: onValueDeleted,
    }
  );

  const isLoading = useMemo(
    () => loading || deletingCoupon,
    [deletingCoupon, loading]
  );

  // const [label, setLabel] = useState<string | undefined>(value?.label);
  // const [description, setDescription] = useState<string | undefined>(
  //   value?.description
  // );
  const [i18nFields, setI18nFields] = useState<
    Partial<i18nForFields<"description">>
  >(value?.i18n ?? {});
  const [targetBuildings, setTargetBuildings] = useState<CouponBuilding[]>(
    value?.targetBuildings ?? []
  );
  const [targetApartments, setTargetApartments] = useState<CouponApartment[]>(
    value?.targetApartments ?? []
  );
  const [targetUsers, setTargetUsers] = useState<CouponTargetUser[]>(
    value?.targetUsers ?? []
  );
  const [service, setService] = useState<CouponService | undefined>(
    value?.service
  );
  const [code, setCode] = useState<string | undefined>(value?.code);
  const [discountInPercentage, setDiscountInPercentage] = useState<
    number | undefined
  >(value?.discountInPercentage);
  const [discountInPrice, setDiscountInPrice] = useState<number | undefined>(
    value?.discountInPrice
  );
  const [listItems, setListItems] = useState<CouponServiceListItem[]>(
    value?.listItems ?? []
  );
  const [maxUseCount, setMaxUseCount] = useState<number | undefined>(
    value?.maxUseCount ?? 1
  );
  const [expireDate, setExpireDate] = useState<Date | null>(
    value?.expireDate ? new Date(value.expireDate) : null
  );
  const [type] = useState<CouponType>(value?.type ?? CouponType.SERVICE);

  const canFillBuildings = useMemo(
    () =>
      [targetApartments?.length === 0, targetUsers?.length === 0].every(
        (v) => v === true
      ),
    [targetApartments.length, targetUsers.length]
  );

  const canFillApartments = useMemo(
    () =>
      [targetBuildings?.length === 0, targetUsers?.length === 0].every(
        (v) => v === true
      ),
    [targetBuildings.length, targetUsers.length]
  );

  const canFillUsers = useMemo(
    () =>
      [targetBuildings?.length === 0, targetApartments?.length === 0].every(
        (v) => v === true
      ),
    [targetBuildings.length, targetApartments.length]
  );

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    invokeCreateOrUpdate({
      variables: {
        id: (value as Coupon)?.id,
        couponData: {
          id: (value as Coupon)?.id,
          targetBuildings,
          targetApartments,
          targetUsers,
          service,
          code,
          discountInPercentage,
          discountInPrice,
          listItems,
          maxUseCount,
          expireDate,
          type,
          i18n: i18nFields,
        },
      },
    });
  };

  const triggerInvokeDeleteCoupon = () => {
    invokeDeleteCoupon({
      variables: {
        id: value?.id,
      },
    });
  };

  return (
    <form
      className={classes.root}
      onSubmit={onFormSubmit}
      noValidate
      autoComplete="off"
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ width: "100%", padding: "0.3rem" }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5">
                  Configurazione dati essenziali del coupon
                </Typography>
              </Grid>
              <Grid item sm={12} xs={12}>
                <I18NTextField
                  required
                  multiline
                  i18nFieldName="description"
                  disabled={isLoading}
                  className={classes.textField}
                  label="Descrizione"
                  variant="outlined"
                  value={i18nFields}
                  onChange={(e) => setI18nFields(e)}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <I18NTextField
                  required
                  multiline
                  i18nFieldName="termsAndConditions"
                  disabled={isLoading}
                  className={classes.textField}
                  label="Termini e condizioni utilizzo coupon"
                  variant="outlined"
                  value={i18nFields}
                  onChange={(e) => setI18nFields(e)}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField
                  required
                  disabled={isLoading}
                  className={classes.textField}
                  label="Codice univoco coupon"
                  variant="outlined"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <DateTimePicker
                  ampm={false}
                  disabled={loading}
                  // disablePast={true}
                  format="dd/MM/yyyy"
                  style={{ width: "100%" }}
                  label="Scadenza"
                  inputVariant="outlined"
                  value={expireDate}
                  onChange={(e) => setExpireDate(e)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">
                  Sconto % e Sconto in Euro possono essere compilati solo in
                  modo esclusivo.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item sm={6} xs={12}>
                    <TextField
                      type="number"
                      disabled={isLoading || (discountInPrice ?? 0) > 0}
                      className={classes.textField}
                      label="Sconto %"
                      variant="outlined"
                      value={discountInPercentage}
                      onChange={(e) => {
                        const numericValue = parseInt(e.target.value, 10);
                        setDiscountInPercentage(
                          numericValue > 0 && numericValue <= 100
                            ? numericValue
                            : numericValue > 100
                            ? 100
                            : 0
                        );
                      }}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <TextField
                      type="number"
                      disabled={isLoading || (discountInPercentage ?? 0) > 0}
                      className={classes.textField}
                      label="Sconto in euro"
                      variant="outlined"
                      value={discountInPrice}
                      onChange={(e) => {
                        const numericValue = parseInt(e.target.value, 10);
                        setDiscountInPrice(numericValue);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField
                  type="number"
                  disabled={isLoading}
                  className={classes.textField}
                  label="Totale utilizzi coupon"
                  variant="outlined"
                  value={maxUseCount}
                  onChange={(e) => {
                    const numericValue = parseInt(e.target.value, 10);
                    setMaxUseCount(numericValue >= 1 ? numericValue : 1);
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={3} style={{ width: "100%", padding: "0.3rem" }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5">
                  Configurazione servizio e prodotti soggetti al coupon
                </Typography>
                <Typography variant="caption">
                  In questa sezione si indicano servizi e prodotti che sono
                  soggetti al coupon.
                </Typography>
              </Grid>
              <Grid item sm={6} xs={12}>
                <GqlRecordPicker
                  multiple={false}
                  className={classes.textField}
                  value={service}
                  getOptionLabel={(record: Service) => record.name}
                  placeHolder={"Servizio"}
                  renderOption={(record: Service) => record.name}
                  onValueChange={(value: Service | null) => {
                    if (value != null) {
                      setService({
                        id: value.id,
                        name: value.name,
                      });
                    } else {
                      setService(void 0);
                    }
                    setListItems([]);
                  }}
                  query={QUERIES.BACKOFFICE_SERVICES}
                  queryResultDataField={"backofficeServices"}
                />
              </Grid>
              {service && (
                <Grid item xs={12}>
                  <GqlRecordPicker
                    multiple={true}
                    className={classes.textField}
                    value={listItems}
                    getOptionLabel={(record: ServiceTypeListItem) =>
                      record.label ?? ""
                    }
                    placeHolder={"Prodotti / Servizi"}
                    renderOption={(record: ServiceTypeListItem) => record.label}
                    onValueChange={(value: ServiceTypeListItem[] | null) => {
                      if (value != null) {
                        setListItems(
                          value.map((v) => ({
                            id: v.id ?? "",
                            label: v.label ?? "",
                          }))
                        );
                      } else {
                        setListItems([]);
                      }
                    }}
                    query={QUERIES.SERVICE_TYPE_LIST_ITEMS}
                    queryVariables={{
                      serviceId: service.id,
                    }}
                    queryResultDataField={"serviceTypeListItems"}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ width: "100%", padding: "0.3rem" }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5">
                  Configurazione destinatari del coupon
                </Typography>
                <Typography variant="caption">
                  Puoi selezionare esclusivamente palazzi, appartamenti o
                  utenti.
                  <br />
                  Selezionando i palazzi, tutti gli appartamenti del palazzo
                  potranno usufruire del coupon.
                  <br />
                  Selezionando gli appartamenti, ogni appartamento selezionato
                  potrà usufruire del coupon.
                  <br />
                  Selezionando gli utenti, ogni utente selezionato potrà
                  usufruire del coupon.
                </Typography>
              </Grid>
              {canFillBuildings && (
                <Grid item xs={12}>
                  <GqlRecordPicker
                    className={classes.textField}
                    multiple={true}
                    value={targetBuildings}
                    getOptionLabel={(record: Building) =>
                      record.code ?? record.name
                    }
                    placeHolder={"Palazzi"}
                    renderOption={(record: Building) =>
                      record.code ?? record.name
                    }
                    onValueChange={(value) =>
                      setTargetBuildings(
                        value.map((b) => ({
                          id: b.id,
                          code: b.code,
                        }))
                      )
                    }
                    query={QUERIES.BUILDINGS("full")}
                    queryResultDataField={"buildings"}
                  />
                </Grid>
              )}
              {canFillApartments && (
                <Grid item xs={12}>
                  <GqlRecordPicker
                    className={classes.textField}
                    multiple={true}
                    value={targetApartments}
                    getOptionLabel={(record: Apartment) =>
                      record.code ?? record.name
                    }
                    placeHolder={"Appartamenti"}
                    renderOption={(record: Apartment) =>
                      record.code ?? record.name
                    }
                    onValueChange={(value) =>
                      setTargetApartments(
                        value.map((b) => ({
                          id: b.id,
                          code: b.code,
                        }))
                      )
                    }
                    query={QUERIES.APARTMENTS}
                    queryResultDataField={"apartments"}
                  />
                </Grid>
              )}
              {canFillUsers && (
                <Grid item xs={12}>
                  <GqlRecordPicker
                    className={classes.textField}
                    multiple={true}
                    value={targetUsers}
                    getOptionLabel={(record: CouponTargetUser | User) =>
                      (record as CouponTargetUser)?.fullName ??
                      computeUserVisualName(record as User)
                    }
                    placeHolder={"Utenti"}
                    renderOption={(record: User) =>
                      computeUserVisualName(record)
                    }
                    onValueChange={(value) =>
                      setTargetUsers(
                        value.map((b) => ({
                          id: b.id,
                          fullName: b.fullName ?? computeUserVisualName(b),
                        }))
                      )
                    }
                    query={QUERIES.USERS}
                    queryResultDataField={"users"}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {value?.id && <CouponUsageDetails coupon={value} />}
        </Grid>
        <Grid item sm={12} xs={12}>
          <Button
            className={classes.textField}
            type="submit"
            variant="contained"
            color="primary"
          >
            {!isLoading ? "Salva" : <CircularProgress />}
          </Button>
        </Grid>
        {value?.id && (
          <Grid item sm={12} xs={12}>
            <ConfirmButtonWithDialog
              className={classes.textField}
              type="button"
              variant="contained"
              color="secondary"
              dialogProps={{
                fullScreen: false,
                title: "Conferma eliminazione",
              }}
              dialogContent={
                "Confermi di voler eliminare definitivamente questo elemento? l'operazione è irreversibile."
              }
              onConfirm={() => triggerInvokeDeleteCoupon()}
              onUndo={() => void 0}
            >
              {!isLoading ? "Elimina" : <CircularProgress />}
            </ConfirmButtonWithDialog>
          </Grid>
        )}
      </Grid>
    </form>
  );
}
